/* eslint-disable no-console */
import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from 'prosemirror-state'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    fileHandler: {
      insertImage: (options: { src: string }) => ReturnType
    }
  }
}

export type FileHandlerOptions = {
  allowedMimeTypes: string[]
  onUpload?: (file: File) => Promise<string>
}

export type FileHandlerExtension = {
  name: 'fileHandler'
  options: FileHandlerOptions
}

export const FileHandler = Extension.create<FileHandlerOptions>({
  name: 'fileHandler',

  addOptions() {
    return {
      allowedMimeTypes: ['image/png', 'image/jpeg', 'image/gif', 'image/webp'],
      onUpload: undefined,
    }
  },

  addCommands() {
    return {
      insertImage:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({
            type: 'image',
            attrs: {
              style: 'max-width: 100%;',
              'data-resizable': 'true',
              ...options,
            },
          })
        },
    }
  },

  addProseMirrorPlugins() {
    const handleUpload = (file: File, pos?: number) => {
      return new Promise((resolve, reject) => {
        if (!this.options.allowedMimeTypes.includes(file.type)) {
          reject(new Error('Tipo de arquivo não permitido'))
          return
        }

        if (this.options.onUpload) {
          this.options
            .onUpload(file)
            .then((src) => {
              this.editor.commands.insertImage({ src })
              resolve(true)
            })
            .catch((error) => {
              console.error('Erro no upload:', error)
              reject(error)
            })
        } else {
          const reader = new FileReader()
          reader.onload = () => {
            const position = pos || this.editor.state.selection.anchor
            this.editor.commands.insertContentAt(position, {
              type: 'image',
              attrs: {
                src: reader.result,
                style: 'max-width: 100%;',
                'data-resizable': 'true',
              },
            })
            resolve(true)
          }
          reader.onerror = (error) => reject(error)
          reader.readAsDataURL(file)
        }
      })
    }

    return [
      new Plugin({
        key: new PluginKey('fileHandler'),
        props: {
          handleDrop: (view, event) => {
            const { files } = event.dataTransfer || { files: [] }
            if (!files || files.length === 0) return false

            const coordinates = view.posAtCoords({
              left: event.clientX,
              top: event.clientY,
            })

            Array.from(files).forEach((file) => {
              handleUpload(file, coordinates?.pos).catch((error) =>
                console.error('Erro no drop:', error)
              )
            })

            return true
          },
          handlePaste: (_, event) => {
            const { files } = event.clipboardData || { files: [] }
            if (!files || files.length === 0) return false

            Array.from(files).forEach((file) => {
              handleUpload(file).catch((error) =>
                console.error('Erro no paste:', error)
              )
            })

            return true
          },
        },
      }),
    ]
  },
})
